/* eslint-disable @typescript-eslint/no-explicit-any */

import 'js-joda';
import 'js-joda-timezone';
import 'ric';
import { Provider } from 'react-redux';
import { applyMiddleware, compose, createStore } from 'redux';
import localePolyfill from '@tock/shared/utils/locales';
import thunkMiddleware from 'redux-thunk';

import ClowncarAppConstants from 'constants/ClowncarAppConstants';

import AppConstants from 'constants/AppConstants';

import { ApolloProvider } from '@apollo/client';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { StoreType } from 'utils/connect';
import { createRoot } from 'react-dom/client';
import { createTockApiMiddleware } from '@tock/shared/middleware/TockApiMiddleware';
import { logger } from '@tock/logging';
import { resizeCallback } from '@tock/shared/utils/responsive';
import AppSettings from 'settings/AppSettings';
import Application from 'Application';

import { initializeApolloClient } from 'initializeApolloClient';
import rootReducer from './state';

require('../css/index.scss');

localePolyfill(false);

logger.init(AppSettings.LOG_LEVEL);
(window as any).TOUCH_ENABLED = 'ontouchstart' in window;

const AllConstants = {
  ...AppConstants,
  ...ClowncarAppConstants,
  ...SharedAppConstants,
};

const getAuthState = (state: StoreType) => {
  return {
    jwtToken: state.app.jwtToken,
    activeAuth: state.app.activeAuth,
    csrfToken: state.app.csrfToken,
  };
};

const logoutAction = () => {
  // window.location.hash only appends a fragment and does not trigger a
  // BrowserRouter route change. Use assign() to perform a real navigation
  // so LogoutRoute mounts and clears auth state before redirecting to /login.
  window.location.assign('/logout');
};

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const store = composeEnhancers(
  applyMiddleware(
    createTockApiMiddleware({
      apiBase: AppSettings.API_BASE,
      appConstants: AllConstants,
      logoutAction,
      getAuthState,
    }),
    thunkMiddleware
  )
)(createStore)(rootReducer);

export type AppDispatch = typeof store.dispatch;

const apolloClient = initializeApolloClient(store);

// Provide the store on the window for modals and easy debugging.
(window as any).store = store;
(window as any).ENABLE_FEATURES_BY_DEFAULT = AppSettings.ENABLE_FEATURES_BY_DEFAULT;
(window as any).app = {
  currency: 'USD',
  locale: 'en-us',
  timeZone: 'US/Central',
};

const resizeCallbackWithStore = () => {
  resizeCallback(false, false);
};

window.addEventListener('resize', resizeCallbackWithStore);
resizeCallback(false, false);

// const StrictMode = (React as any).StrictMode;

function run() {
  let element = document.getElementById('Root');

  // This is used for tests to render without a main element.
  if (!element) {
    element = document.createElement('div');
    document.body.appendChild(element);
  }

  if (!(window as any).TOUCH_ENABLED) {
    const body = document.querySelector('body');
    if (body) {
      body.classList.add('no-touch');
    }
  }

  const root = createRoot(element);

  const renderApp = (App: any) => {
    root.render(
      // <StrictMode>
      <Provider store={store}>
        <ApolloProvider client={apolloClient}>
          <App />
        </ApolloProvider>
      </Provider>
      // </StrictMode>,
    );
  };

  renderApp(Application);
}

// See if the document loaded. If so kick off fast load.
if (document.getElementById('Root')) {
  run();
} else if (window.addEventListener) {
  // Else kick off a delayed load.
  window.addEventListener('DOMContentLoaded', run);
} else {
  (window as any).attachEvent('onload', run);
}
